import React, { useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { isNil } from 'ramda'
import { Global, css } from '@emotion/core'
import {
  DEFAULT_DRAWER_WIDTH,
  DRAWER_TRANSITION_MS,
} from 'utils/drawerConstants'
import { classNames } from 'utils/utils'
import { CSSTransition } from 'react-transition-group'
import Icon from 'components/patterns/Icon'

import {
  StyledDrawer,
  Mask,
  Tab,
  DrawerHeader,
  DrawerHeaderRow,
} from './Drawer.style'

/* eslint-disable react-hooks/exhaustive-deps */
const Drawer = ({
  className,
  children,
  fullscreen,
  placement,
  showTab,
  onEnter,
  onEntered,
  onExit,
  noHeaderSpacing,
  onExited,
  header,
  // TODO: can move out when all drawers use redux
  setIsDrawerOpening,
  setIsDrawerOpened,
  setIsDrawerClosing,
  setIsDrawerClosed,
  setIsDrawerVisible,
  isDrawerVisible,
  showDrawerMask,
  drawerWidth,
  visibleWidth,
  contrastBkg,
}) => {
  const handleEnter = () => {
    setIsDrawerOpening()
    onEnter()
  }
  const handleEntered = () => {
    setIsDrawerOpened()
    onEntered()
  }
  const handleExit = () => {
    setIsDrawerClosing()
    onExit()
  }
  const handleExited = () => {
    setIsDrawerClosed()
    onExited()
  }

  const handleClickToggle = useCallback(() => {
    setIsDrawerVisible(!isDrawerVisible)
  }, [isDrawerVisible])

  const renderToggle = useMemo(() => {
    if (fullscreen || !showTab) return null

    return (
      !showDrawerMask && (
        <Tab onClick={handleClickToggle} data-testid="drawer-tab">
          {/* TODO: investigation needed: Using a styled icon breaks in build. That's why the color is here hardcoded this way. */}
          <Icon
            icon={isDrawerVisible ? 'FaCaretRight' : 'FaCaretLeft'}
            color="blue"
            style={{ width: '18px', height: '18px' }}
          />
        </Tab>
      )
    )
  }, [fullscreen, isDrawerVisible, showDrawerMask, showTab])

  // Owns BOTH completion paths so cleanup is unconditional.
  //
  // `transitionend` bubbles, so a child's transition (a button hover, a spinner)
  // would otherwise complete the drawer's transition early — only the drawer's own
  // transform counts. And when no qualifying event ever arrives (transform
  // unchanged, reduced motion, hidden node) the fallback timer settles it.
  //
  // The fallback lives here rather than in CSSTransition's `timeout` prop: react-
  // transition-group races its own setTimeout against this listener and whichever
  // wins leaves the loser in place, so a timeout-driven completion would strand the
  // event listener on a drawer that stays mounted across open/close cycles. `timeout`
  // is documented as "required unless addEndListener is provided", so omitting it is
  // supported. `settle` is idempotent, so whichever path fires first wins and the
  // other is torn down.
  const transitionEndListener = (node, done) => {
    let settled = false
    let fallbackTimer = null

    const settle = (event) => {
      if (settled) return
      settled = true
      clearTimeout(fallbackTimer)
      node.removeEventListener('transitionend', onDrawerTransitionEnd, false)
      done(event)
    }

    // Function declaration (not const) so `settle` can reference it above.
    function onDrawerTransitionEnd(event) {
      if (event.target !== node || event.propertyName !== 'transform') return
      settle(event)
    }

    node.addEventListener('transitionend', onDrawerTransitionEnd, false)
    fallbackTimer = setTimeout(settle, DRAWER_TRANSITION_MS)
  }

  const drawerClassNames = (state) =>
    classNames(`drawer-${state}`, className, {
      [`wide-${drawerWidth}`]:
        drawerWidth !== DEFAULT_DRAWER_WIDTH && !fullscreen,
    })

  const toggleSecondaryAnimation = useMemo(
    () => isDrawerVisible && !fullscreen && visibleWidth === drawerWidth,
    [isDrawerVisible, fullscreen, visibleWidth, drawerWidth]
  )

  const showHeader = !isNil(header)

  return (
    <>
      {isDrawerVisible && fullscreen && (
        <Global
          styles={css`
            body {
              overflow: hidden;
            }
          `}
        />
      )}
      {showDrawerMask && <Mask />}
      <CSSTransition
        in={isDrawerVisible}
        classNames="drawer"
        onEnter={handleEnter}
        onEntered={handleEntered}
        onExit={handleExit}
        onExited={handleExited}
        addEndListener={transitionEndListener}
        // No `timeout` prop on purpose. It previously held 0, which beat the 300ms
        // transitionend and fired onExited immediately — consumers that unmount on
        // onExited tore the drawer out before it had slid (Issue 1). Setting it to
        // the CSS duration fixed that but left two competing completion paths, and
        // a timeout-driven win stranded the event listener. transitionEndListener
        // now owns the fallback so there is exactly one path and one cleanup.
      >
        {(
          state // TODO: add CSSTransition and use Event handlers to set smooth transition between wide/slim drawer, when time permits
        ) => (
          <CSSTransition
            in={toggleSecondaryAnimation}
            classNames="drawerExpanded"
            timeout={0}
          >
            {(secondaryState) => (
              <StyledDrawer
                fullscreen={fullscreen}
                visibleWidth={visibleWidth}
                drawerWidth={drawerWidth}
                showTab={showTab}
                className={classNames(
                  drawerClassNames(state),
                  `drawerExpanded-${secondaryState}`
                )}
                placement={placement}
                contrastBkg={contrastBkg}
                data-testid="drawer"
              >
                {showHeader &&
                  (noHeaderSpacing ? (
                    <DrawerHeaderRow>{header}</DrawerHeaderRow>
                  ) : (
                    <DrawerHeader type="h1">{header}</DrawerHeader>
                  ))}
                {renderToggle}
                {children}
              </StyledDrawer>
            )}
          </CSSTransition>
        )}
      </CSSTransition>
    </>
  )
}

Drawer.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  showTab: PropTypes.bool,
  fullscreen: PropTypes.bool,
  placement: PropTypes.oneOf(['left', 'right']),
  onEnter: PropTypes.func,
  onEntered: PropTypes.func,
  onExit: PropTypes.func,
  onExited: PropTypes.func,
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  // TODO: can be in redux
  setIsDrawerOpening: PropTypes.func,
  setIsDrawerOpened: PropTypes.func,
  setIsDrawerClosing: PropTypes.func,
  setIsDrawerClosed: PropTypes.func,
  setIsDrawerVisible: PropTypes.func,
  isDrawerVisible: PropTypes.bool,
  noHeaderSpacing: PropTypes.bool,
  showDrawerMask: PropTypes.bool,
  /** the total width of the drawer */
  drawerWidth: PropTypes.number,
  /** the width visible to the user */
  visibleWidth: PropTypes.number,
  /** change background from white to modelGrey */
  /** could go away if all fullwidth drawers use this color */
  contrastBkg: PropTypes.bool,
}

Drawer.defaultProps = {
  children: '',
  fullscreen: false,
  placement: 'right',
  showTab: false,
  onEnter: () => {},
  onEntered: () => {},
  onExit: () => {},
  onExited: () => {},
  header: null,
  visibleWidth: DEFAULT_DRAWER_WIDTH,
  drawerWidth: DEFAULT_DRAWER_WIDTH,
}

export default Drawer
